import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { TypeContrato } from '@/features/agreement/utils/enum'

const regimeParaProdutosMap: Record<string, string[]> = {
  [TypeContrato.SIMPLES_NACIONAL]: [
    TypeContrato.APURACAO_ST,
    TypeContrato.PIS_COFINS_MONOFASICO_PARA_SN,
    TypeContrato.NEGOCIACAO_DE_PASSIVOS
  ],
  [TypeContrato.LUCRO_REAL]: [
    TypeContrato.PREVIDENCIARIO,
    TypeContrato.NEGOCIACAO_DE_PASSIVOS,
    TypeContrato.INSS_MAO_DE_OBRA,
    TypeContrato.EXCLUSAO_DE_ICMS_E_ST,
    TypeContrato.ALIQUOTA_ZERO_COMBUSTIVEL,
    TypeContrato.SUBVENCAO
    ,TypeContrato.FCVS
    // ,TypeContrato.MVA
    // ,TypeContrato.ALIQUOTA_ZERO
  ],
  [TypeContrato.LUCRO_PRESUMIDO]: [
    TypeContrato.PREVIDENCIARIO,
    TypeContrato.NEGOCIACAO_DE_PASSIVOS,
    TypeContrato.INSS_MAO_DE_OBRA,
    TypeContrato.EXCLUSAO_DE_ICMS_E_ST,
    TypeContrato.EQUIPARACAO_HOSPITALAR
  ]
}

export function getProdutosValidosParaRegime(regime: string): string[] {
  return regimeParaProdutosMap[regime] || []
}

interface FormRegimeProdutoProps {
  modo: 'editar' | 'adicionar'
  regimeTributario: string | null
  produtosSelecionados: string[]
  produtosFixos: string[]
  regimesDisponiveis: string[]
  regimesAtivos: string[]
  onRegimeChange: (regime: string) => void
  onProdutoChange: (produto: string) => void
  onSubmit: () => void
}

export function FormRegimeProduto({
  modo,
  regimeTributario,
  produtosSelecionados,
  produtosFixos,
  regimesDisponiveis,
  regimesAtivos,
  onRegimeChange,
  onProdutoChange,
  onSubmit
}: FormRegimeProdutoProps) {
  const [produtosDisponiveisOpen, setProdutosDisponiveisOpen] = useState(false)

  const produtosDisponiveis = regimeTributario ? getProdutosDisponiveis(regimeTributario as TypeContrato) : []
  useEffect(() => {
    setProdutosDisponiveisOpen(false)
  }, [modo])
  const handleRegimeSelection = (value: string) => {
    onRegimeChange(value)

    setProdutosDisponiveisOpen(true)
  }

  return (
    <Card className="w-full max-w-2xl bg-background text-foreground">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">
          {modo === 'editar' ? 'Editar Regime Atual' : 'Adicionar Novo Regime'}
        </CardTitle>
        <CardDescription className="text-center">
          {modo === 'editar'
            ? 'Edite os produtos vinculados ao regime atual.'
            : 'Selecione um regime disponível e configure os produtos.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Regime Tributário */}
        <div>
          <h3 className="text-lg font-semibold mb-2">Regime Tributário</h3>

          {modo === 'editar' ? (
            <RegimeSelector
              regimes={regimesAtivos}
              selectedRegime={regimeTributario}
              onChange={handleRegimeSelection}
            />
          ) : (
            <RadioGroup value={regimeTributario ?? undefined} onValueChange={handleRegimeSelection}>
              {regimesDisponiveis.map(regime => (
                <div key={regime} className="flex items-center space-x-2">
                  <RadioGroupItem value={regime} id={regime} />
                  <Label htmlFor={regime}>{getLabelRegimeTributario(regime as TypeContrato)}</Label>
                </div>
              ))}
            </RadioGroup>
          )}
        </div>

        {/* Produtos Disponíveis */}
        <div>
          <h3 className="text-lg font-semibold mb-2">Produtos Disponíveis</h3>

          {regimeTributario && produtosDisponiveis.length > 0 ? (
            produtosDisponiveisOpen ? (
              <div className="space-y-2">
                {produtosDisponiveis.map(produto => {
                  const isFixed = produtosFixos.includes(produto)
                  return (
                    <div key={produto} className="flex items-center space-x-2">
                      <Checkbox
                        id={produto}
                        checked={produtosSelecionados.includes(produto)}
                        disabled={modo === 'editar' && isFixed}
                        onCheckedChange={() => onProdutoChange(produto)}
                      />
                      <Label htmlFor={produto}>{formatarNomeProduto(produto)}</Label>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Selecione um regime tributário para listar produtos</p>
            )
          ) : (
            <p className="text-sm text-gray-500">Nenhum produto disponível para esse regime</p>
          )}
        </div>

        {/* Botão */}
        <div className="flex justify-center">
          <Button
            type="button"
            onClick={onSubmit}
            className="bg-primary hover:bg-primary-hover text-primary-foreground"
          >
            {modo === 'editar' ? 'Atualizar Produtos' : 'Adicionar Regime'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// funções auxiliares
function getLabelRegimeTributario(regime: TypeContrato): string {
  switch (regime) {
    case TypeContrato.LUCRO_REAL:
      return 'Lucro Real'
    case TypeContrato.LUCRO_PRESUMIDO:
      return 'Lucro Presumido'
    case TypeContrato.SIMPLES_NACIONAL:
      return 'Simples Nacional'
    default:
      return 'Não Selecionado'
  }
}

function formatarNomeProduto(produto: string): string {
  return produto
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, l => l.toUpperCase())
}

function getProdutosDisponiveis(regimeTributario?: TypeContrato): TypeContrato[] {
  if (regimeTributario === TypeContrato.SIMPLES_NACIONAL) {
    return [TypeContrato.PIS_COFINS_MONOFASICO_PARA_SN, TypeContrato.APURACAO_ST, TypeContrato.NEGOCIACAO_DE_PASSIVOS]
  }
  if (regimeTributario === TypeContrato.LUCRO_PRESUMIDO) {
    return [
      TypeContrato.PREVIDENCIARIO,
      TypeContrato.NEGOCIACAO_DE_PASSIVOS,
      TypeContrato.EQUIPARACAO_HOSPITALAR,
      TypeContrato.EXCLUSAO_DE_ICMS_E_ST
    ]
  }
  if (regimeTributario === TypeContrato.LUCRO_REAL) {
    return [
      TypeContrato.PREVIDENCIARIO,
      TypeContrato.NEGOCIACAO_DE_PASSIVOS,
      TypeContrato.EXCLUSAO_DE_ICMS_E_ST,
      TypeContrato.SUBVENCAO,
      TypeContrato.FCVS
    ]
  }
  return []
}

function RegimeSelector({
  regimes,
  selectedRegime,
  onChange
}: {
  regimes: string[]
  selectedRegime: string | null
  onChange: (regime: string) => void
}) {
  return (
    <div className="flex flex-col items-start mb-6">
      <label className="text-sm font-semibold mb-1">Selecione o Regime para Editar:</label>
      <select
        className="border rounded px-2 py-1 text-foreground bg-background"
        value={selectedRegime ?? ''}
        onChange={e => onChange(e.target.value)}
      >
        <option value="" disabled>
          Selecione...
        </option>
        {regimes.map(regime => (
          <option key={regime} value={regime}>
            {getLabelRegimeTributario(regime as TypeContrato)}
          </option>
        ))}
      </select>
    </div>
  )
}
